import { useKycVerificationContext } from "../../Contexts/KycVerificationContext";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import React, { useRef, useState } from "react";
import { IconButton } from "@mui/material";

function CaptureSelf() {
  const { isMobile, formData, setFormData, errorMessage, setErrorMessage } =
    useKycVerificationContext();

  const [videoStarted, setVideoStarted] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleSetPhoto = (photo) => {
    let newForm = {
      ...formData,
      selfie: photo,
    };
    setFormData(newForm);
    setErrorMessage("");
  };

  const handleChooseFile = (e) => {
    let file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          // Define target size and quality
          const targetSizeKB = 200;
          const maxWidthHeight = 1080; // Initial maximum dimension

          // Function to compress image
          const compressImage = (image, width, height, quality) => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(image, 0, 0, width, height);
            return canvas.toDataURL("image/jpeg", quality);
          };

          // Resize image to maintain 1:1 aspect ratio and check size
          const resizeImage = (image, maxDimension, quality) => {
            let width = img.width;
            let height = img.height;
            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
              } else {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
              }
            } else {
              width = Math.min(width, height);
              height = width;
            }
            return compressImage(image, width, height, quality);
          };

          let quality = 0.7;
          let optimizedImage = resizeImage(img, maxWidthHeight, quality);

          // Check if the image size is within the target size
          const checkSizeAndAdjust = (dataUrl, callback) => {
            const imageSizeKB = (dataUrl.length * 3) / 4 / 1024; // Approximate size in KB
            if (imageSizeKB > targetSizeKB && quality > 0.1) {
              quality -= 0.1; // Reduce quality
              const image = new Image();
              image.src = dataUrl;
              image.onload = () => {
                const newDataUrl = resizeImage(image, maxWidthHeight, quality);
                checkSizeAndAdjust(newDataUrl, callback);
              };
            } else {
              callback(dataUrl);
            }
          };

          checkSizeAndAdjust(optimizedImage, handleSetPhoto);
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const startVideo = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch((err) => {
              console.error("Error playing video:", err);
              setErrorMessage(
                "Error starting video playback. Please ensure your camera is accessible."
              );
            });
          };
          videoRef.current.onerror = (err) => {
            console.error("Video error:", err);
            setErrorMessage("Error loading video. Please try again.");
          };
          setVideoStarted(true);
        }
      } else {
        setErrorMessage("Media devices are not supported in your browser.");
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      if (err.name === "NotAllowedError") {
        setErrorMessage(
          "Camera access was denied. Please allow camera access."
        );
      } else if (err.name === "NotFoundError") {
        setErrorMessage("No camera device found.");
      } else {
        setErrorMessage(
          "Unable to access the camera. Please check your permissions."
        );
      }
    }
  };

  const handleCapture = async () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const video = videoRef.current;

      // Set the canvas dimensions to match the video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get the image data URL from the canvas
      let imgDataUrl = canvas.toDataURL("image/jpeg");

      // Function to compress and resize the image
      const processImage = (
        dataUrl,
        maxDimension,
        targetSizeKB,
        qualityStep = 0.1
      ) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = dataUrl;
          img.onload = () => {
            let width = img.width;
            let height = img.height;

            // Resize to maintain a 1:1 ratio
            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
              } else {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
              }
            } else {
              width = Math.min(width, height);
              height = width;
            }

            // Function to compress image
            const compressImage = (image, width, height, quality) => {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(image, 0, 0, width, height);
              return canvas.toDataURL("image/jpeg", quality);
            };

            let quality = 0.7;
            let compressedDataUrl = compressImage(img, width, height, quality);

            // Check image size and adjust quality if needed
            const checkSizeAndAdjust = (dataUrl, callback) => {
              const imageSizeKB = (dataUrl.length * 3) / 4 / 1024; // Approximate size in KB
              if (imageSizeKB > targetSizeKB && quality > 0.1) {
                quality -= qualityStep; // Reduce quality
                const newImg = new Image();
                newImg.src = dataUrl;
                newImg.onload = () => {
                  const newDataUrl = compressImage(
                    newImg,
                    width,
                    height,
                    quality
                  );
                  checkSizeAndAdjust(newDataUrl, callback);
                };
              } else {
                callback(dataUrl);
              }
            };

            checkSizeAndAdjust(compressedDataUrl, resolve);
          };
        });
      };

      // Process the image with resizing and compression
      const optimizedImage = await processImage(imgDataUrl, 1080, 200);
      handleSetPhoto(optimizedImage);

      // Stop video stream
      const stream = video.srcObject;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
        setVideoStarted(false);
      }
    } else {
      setErrorMessage("Unable to capture image. Video is not available.");
    }
  };

  let errMsg = errorMessage && (
    <p className="font-bold text-red-600 mb-2">{errorMessage}</p>
  );

  return (
    <div className="p-4">
      <p className="text-[#1F2328] text-xl font-bold mb-1">Take a Selfie</p>
      <p className="text-[#60656D] mb-4">
        Take a selfie of your face with clear visibility, ensuring good lighting
        and a focused image.
      </p>
      {errMsg}
      <div className="flex min-h-[400px] rounded-lg overflow-hidden relative bg-[#edf0f6] mb-4">
        {formData.selfie ? (
          <img src={formData.selfie} className="w-full h-full" alt="selfie" />
        ) : !isMobile ? (
          <video ref={videoRef} className="w-full h-full" />
        ) : null}

        {isMobile && (
          <div>
            <input
              id="selfie"
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={handleChooseFile}
            />
            <label
              htmlFor="selfie"
              className="cursor-pointer flex items-center justify-center absolute bottom-2 right-2
                         w-12 h-12 z-1 bg-[#0947ab] text-white rounded-full"
            >
              <CameraAltIcon />
            </label>
          </div>
        )}
        {!isMobile && formData.selfie === "" && (
          <div className="absolute bottom-2 right-2">
            {!videoStarted ? (
              <IconButton
                onClick={startVideo}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#0947ab",
                  color: "white",
                  borderRadius: "50%",
                  zIndex: 1,
                  "&:hover": {
                    backgroundColor: "#073a7d",
                  },
                }}
              >
                <CameraAltIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={handleCapture}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#0947ab",
                  color: "white",
                  borderRadius: "50%",
                  zIndex: 1,
                  "&:hover": {
                    backgroundColor: "#073a7d",
                  },
                }}
              >
                <CameraAltIcon />
              </IconButton>
            )}
          </div>
        )}
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {!isMobile && formData.selfie !== "" ? (
        <div className="flex justify-center items-center mt-2">
          <button
            className="p-1 px-3 rounded-lg bg-[#f7f7f7] border shadow-xs"
            onClick={async () => {
              setFormData((prevData) => ({
                ...prevData,
                selfie: "",
              }));
              startVideo();
            }}
          >
            Retake
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default CaptureSelf;
